<template>
  <v-tooltip
    location="top"
    color="color-neutral-90"
    :disabled="isDragging"
    open-delay="1000"
    transition="scale-transition"
    min-width="500">
    <template v-slot:activator="{ props }">
      <!-- This case when both are at the spot, but detached, they will share the rectangle half/half -->
      <template v-if="assetVisit && assetContainer">
        <div class="full-height" :data-testid="`yard-spot-assigned-${spot.code}`" v-bind="props">
          <svg-asset-container v-show="!isDragging" :scale="0.7" class="mt-1" />
          <v-divider v-show="!isDragging" />
          <yard-view-truck :scale="0.7" :asset-visit="assetVisit" />
        </div>
      </template>

      <!-- And this is when either a container or truck is at the spot, not at the same time -->
      <template v-else>
        <div class="full-height" :data-testid="`yard-spot-assigned-${spot.code}`" v-bind="props">
          <template v-if="assetVisit">
            <div class="d-flex space-between gap-1 justify-center">
              <v-icon
                v-if="appointment?.id"
                size="x-small"
                :color="loadTypeDirectionColor"
                :class="`spot-type-${spot.type} mb-1`">
                {{ getSpotIcon }}
              </v-icon>
              <v-icon
                v-if="assetVisit.assetContainers?.length === 1"
                size="x-small"
                :color="getAssetVisitContainerStatusColor(assetVisit.assetContainers[0])"
                class="mb-1">
                {{ getAssetVisitContainerStatusIcon(assetVisit.assetContainers[0]) }}
              </v-icon>
            </div>
            <yard-view-truck :scale="0.9" :asset-visit="assetVisit" />
          </template>

          <template v-else-if="assetContainer">
            <v-icon size="x-small" :color="assetContainerStatusColor" class="mb-1">
              {{ assetContainerStatusIcon }}
            </v-icon>
            <v-icon
              v-if="assetContainer.pickupAppointment"
              size="x-small"
              color="info"
              class="mb-1 ml-1">
              mdi-calendar
            </v-icon>
            <svg-asset-container class="mt-1" />
          </template>
        </div>
      </template>
    </template>
    <yard-view-spot-tooltip :spot="spot" :appointment="appointment" />
  </v-tooltip>
</template>

<script>
import { computed, defineComponent } from 'vue';
import useYard from '@/modules/yard/composables/useYard';
import YardViewSpotTooltip from '@/modules/yard/components/Spot/Card/YardViewSpotTooltip.vue';
import YardViewTruck from '../../AssetVisit/YardViewTruck.vue';
import { AssetVisitType } from '@nova/core';

export default defineComponent({
  name: 'YardViewSpotAssigned',
  components: { YardViewSpotTooltip, YardViewTruck },
  props: {
    spot: {
      type: Object,
      required: true
    },
    appointment: {
      type: Object,
      required: false
    },
    isDragging: {
      type: Boolean,
      required: true
    }
  },
  setup(props) {
    const yardStore = useYard();
    const assetVisit = computed(() => props.spot.spotAssignments[0]?.assetVisit);
    const assetContainer = computed(() => props.spot.spotAssignments[0]?.assetContainer);
    const assetContainerStatus = computed(() =>
      yardStore.getAssignedAssetContainerStatus(assetContainer.value?.assetContainerEvents)
    );

    const getSpotIcon = computed(() =>
      yardStore.getAssignedSpotIcon(props.appointment?.loadType?.direction)
    );

    const loadTypeDirectionColor = computed(() =>
      yardStore.getLoadTypeDirectionColor(props.appointment?.loadType?.direction)
    );

    const assetContainerStatusIcon = computed(() => {
      return yardStore.getAssetContainerStatusIcon(assetContainerStatus.value);
    });

    const assetContainerStatusColor = computed(() => {
      return yardStore.getAssetContainerStatusColor(assetContainerStatus.value);
    });

    const getAssetVisitContainerStatusIcon = assetContainer => {
      const assetContainerEvents = assetContainer.assetContainerEvents;
      const assetContainerStatus = yardStore.getAssignedAssetContainerStatus(assetContainerEvents);

      return yardStore.getAssetContainerStatusIcon(assetContainerStatus);
    };

    const getAssetVisitContainerStatusColor = assetContainer => {
      const assetContainerEvents = assetContainer.assetContainerEvents;
      const assetContainerStatus = yardStore.getAssignedAssetContainerStatus(assetContainerEvents);

      return yardStore.getAssetContainerStatusColor(assetContainerStatus);
    };

    const isLiveVisit = computed(() => {
      return assetVisit.value?.visitType === AssetVisitType.Live;
    });

    return {
      assetContainerStatusIcon,
      assetContainerStatusColor,
      getAssetVisitContainerStatusIcon,
      getAssetVisitContainerStatusColor,
      assetVisit,
      assetContainer,
      getSpotIcon,
      loadTypeDirectionColor,
      isLiveVisit,
      getSpotStatus: yardStore.getSpotStatus
    };
  }
});
</script>
